import { Group, SimpleGrid, Stack, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';

import type { ContractLine } from '@/api/generated/models';
import { useListContractLines } from '@/api/generated/contracts/contracts';
import { useListUnits } from '@/api/generated/units/units';
import { useListLineAllocations } from '@/api/generated/work-structure/work-structure';
import { IconFigmaCheck } from '@/assets/icons';
import {
  FormModal,
  MobileCard,
  MobileCodeBadge,
  MobileField,
  StatusBanner
} from '@/components/common';
import { findUnitLabel } from '@/features/units/unitDisplay';

interface ContractLineAllocationModalProps {
  opened: boolean;
  onClose: () => void;
  contractId: string;
}

export const ContractLineAllocationModal = ({
  opened,
  onClose,
  contractId
}: ContractLineAllocationModalProps) => {
  const { t, i18n } = useTranslation();

  const { data: linesData } = useListContractLines(contractId, { query: { enabled: opened } });
  const lines: ContractLine[] = (linesData ?? []).filter((line) => !line.validTo);
  const { data: unitsData } = useListUnits();
  const { data: allocationsData } = useListLineAllocations(contractId, {
    query: { enabled: opened }
  });
  const allocatedQtyByLineId = new Map(
    (allocationsData ?? []).map((a) => [a.contractLineId, a.allocatedQty ?? 0])
  );

  // Razrada = ugovor za obje vrste ugovora — sve stavke trebaju biti raspoređene.
  const allAllocated =
    lines.length > 0 &&
    lines.every((line) => (allocatedQtyByLineId.get(line.id ?? '') ?? 0) >= (line.quantity ?? 0));

  return (
    <FormModal
      opened={opened}
      onClose={onClose}
      size={760}
      title={
        <Text fz={20} fw={700} style={{ color: 'var(--color-text-primary)' }}>
          {t('contractLineAllocationModal.title')}
        </Text>
      }
      styles={{
        content: { backgroundColor: 'var(--color-bg-surface)' },
        header: {
          backgroundColor: 'var(--color-bg-surface)',
          borderBottom: '1px solid var(--color-border-soft)'
        },
        body: { padding: 20 }
      }}
    >
      <Stack gap={16}>
        {allAllocated && (
          <StatusBanner
            tone="success"
            icon={<IconFigmaCheck size={16} />}
            message={t('contractLineAllocationModal.allAllocated')}
          />
        )}

        <Stack gap={10} hiddenFrom="sm">
          {lines.length === 0 && (
            <Text fz={13} py={24} ta="center" style={{ color: 'var(--color-text-muted)' }}>
              {t('contractLineAllocationModal.emptyState')}
            </Text>
          )}
          {lines.map((line) => (
            <MobileCard key={line.id}>
              <MobileCodeBadge>
                {t('common.codeBadge', { code: line.itemCode ?? '—' })}
              </MobileCodeBadge>
              <MobileField label={t('contractLineAllocationModal.colDescription')}>
                <Text fz={13.5} fw={600} style={{ color: 'var(--color-text-primary)' }}>
                  {line.description}
                </Text>
              </MobileField>
              <SimpleGrid cols={3} spacing={8}>
                <MobileField label={t('contractLineAllocationModal.colAllocated')}>
                  <Text fz={13} fw={600} style={{ color: 'var(--color-text-primary)' }}>
                    {allocatedQtyByLineId.get(line.id ?? '') ?? 0}
                  </Text>
                </MobileField>
                <MobileField label={t('contractLineAllocationModal.colContracted')}>
                  <Text fz={13} style={{ color: 'var(--color-text-secondary)' }}>
                    {line.quantity}
                  </Text>
                </MobileField>
                <MobileField label={t('contractLineAllocationModal.colUnit')}>
                  <Text fz={13} style={{ color: 'var(--color-text-secondary)' }}>
                    {findUnitLabel(unitsData, line.unitId, i18n.language)}
                  </Text>
                </MobileField>
              </SimpleGrid>
            </MobileCard>
          ))}
        </Stack>

        <Stack gap={0} visibleFrom="sm" style={{ maxHeight: 420, overflowY: 'auto' }}>
          <Group
            gap={8}
            wrap="nowrap"
            py={4}
            style={{ borderBottom: '1px solid var(--color-border-soft)' }}
          >
            <Text fz={11} fw={600} w={90} style={{ color: 'var(--color-text-muted)' }}>
              {t('contractLineAllocationModal.colCode')}
            </Text>
            <Text fz={11} fw={600} style={{ flex: 1, color: 'var(--color-text-muted)' }}>
              {t('contractLineAllocationModal.colDescription')}
            </Text>
            <Text fz={11} fw={600} w={90} ta="right" style={{ color: 'var(--color-text-muted)' }}>
              {t('contractLineAllocationModal.colAllocated')}
            </Text>
            <Text fz={11} fw={600} w={90} ta="right" style={{ color: 'var(--color-text-muted)' }}>
              {t('contractLineAllocationModal.colContracted')}
            </Text>
            <Text fz={11} fw={600} w={60} ta="center" style={{ color: 'var(--color-text-muted)' }}>
              {t('contractLineAllocationModal.colUnit')}
            </Text>
          </Group>

          {lines.length === 0 && (
            <Text fz={13} py={24} ta="center" style={{ color: 'var(--color-text-muted)' }}>
              {t('contractLineAllocationModal.emptyState')}
            </Text>
          )}

          {lines.map((line, index) => (
            <Group
              key={line.id}
              gap={8}
              wrap="nowrap"
              py={10}
              style={{
                borderTop: index === 0 ? undefined : '1px solid var(--color-border-soft)'
              }}
            >
              <Text fz={13} fw={600} w={90} style={{ color: 'var(--color-text-primary)' }}>
                {line.itemCode}
              </Text>
              <Text fz={13} style={{ flex: 1, color: 'var(--color-text-secondary)' }} truncate>
                {line.description}
              </Text>
              <Text fz={13} w={90} ta="right" style={{ color: 'var(--color-text-secondary)' }}>
                {allocatedQtyByLineId.get(line.id ?? '') ?? 0}
              </Text>
              <Text fz={13} w={90} ta="right" style={{ color: 'var(--color-text-secondary)' }}>
                {line.quantity}
              </Text>
              <Text fz={13} w={60} ta="center" style={{ color: 'var(--color-text-secondary)' }}>
                {findUnitLabel(unitsData, line.unitId, i18n.language)}
              </Text>
            </Group>
          ))}
        </Stack>
      </Stack>
    </FormModal>
  );
};
